(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-md-product]';
  const t = (key, fallback = '', replacements = {}) => window.MDT ? window.MDT(key, fallback, replacements) : fallback;

  function toPositiveNumber(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : fallback;
  }

  function pauseMedia(panel) {
    if (!panel) return;

    panel.querySelectorAll('video').forEach((video) => {
      if (typeof video.pause === 'function') video.pause();
    });

    panel.querySelectorAll('iframe').forEach((iframe) => {
      const currentSource = iframe.getAttribute('src');
      if (currentSource) iframe.setAttribute('src', currentSource);
    });
  }

  function initGallery(root) {
    const gallery = root.querySelector('[data-md-gallery]');
    if (!gallery) return null;

    const panels = Array.from(
      gallery.querySelectorAll('[data-md-media-panel]')
    );
    const thumbs = Array.from(
      gallery.querySelectorAll('[data-md-media-thumb]')
    );

    const showMedia = (mediaId, shouldFocus = false) => {
      const targetId = String(mediaId || '');
      if (!targetId) return;

      let activePanel = null;

      panels.forEach((panel) => {
        const isActive = String(panel.dataset.mdMediaPanel) === targetId;

        if (!isActive && !panel.hidden) pauseMedia(panel);

        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
        if (isActive) activePanel = panel;
      });

      thumbs.forEach((thumb) => {
        const isActive = String(thumb.dataset.mdMediaThumb) === targetId;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-pressed', String(isActive));

        if (isActive) {
          thumb.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
            behavior: 'smooth'
          });
        }
      });

      if (shouldFocus && activePanel) {
        const focusTarget = activePanel.querySelector('button, video, iframe');
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      }
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        showMedia(thumb.dataset.mdMediaThumb);
      });
    });

    return { showMedia };
  }

  function initZoom(root) {
    const dialog = root.querySelector('[data-md-zoom-dialog]');
    const zoomImage = dialog
      ? dialog.querySelector('[data-md-zoom-image]')
      : null;

    if (!dialog || !zoomImage) return;

    const closeDialog = () => {
      if (typeof dialog.close === 'function' && dialog.open) {
        dialog.close();
      }
    };

    root.querySelectorAll('[data-md-zoom-open]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const source = trigger.dataset.mdZoomSrc;
        if (!source) return;

        zoomImage.src = source;
        zoomImage.alt = trigger.dataset.mdZoomAlt || '';

        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        }
      });
    });

    dialog
      .querySelectorAll('[data-md-zoom-close]')
      .forEach((button) => button.addEventListener('click', closeDialog));

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });

    dialog.addEventListener('close', () => {
      zoomImage.removeAttribute('src');
      zoomImage.alt = '';
    });
  }

  function setQuantityRules(root, option) {
    const input = root.querySelector('[data-md-quantity-input]');
    if (!input || !option) return;

    const minimum = toPositiveNumber(option.dataset.quantityMin, 1);
    const increment = toPositiveNumber(option.dataset.quantityStep, 1);
    const maximum = Number(option.dataset.quantityMax);

    input.min = String(minimum);
    input.step = String(increment);

    if (Number.isFinite(maximum) && maximum > 0) {
      input.max = String(maximum);
    } else {
      input.removeAttribute('max');
    }

    let current = Number(input.value);
    if (!Number.isFinite(current) || current < minimum) current = minimum;
    if (Number.isFinite(maximum) && maximum > 0) {
      current = Math.min(current, maximum);
    }

    const offset = Math.max(0, Math.round((current - minimum) / increment));
    input.value = String(minimum + offset * increment);
  }

  function initQuantity(root) {
    const input = root.querySelector('[data-md-quantity-input]');
    if (!input) return;

    const normalizeQuantity = () => {
      const minimum = toPositiveNumber(input.min, 1);
      const increment = toPositiveNumber(input.step, 1);
      const maximum = Number(input.max);
      let current = Number(input.value);

      if (!Number.isFinite(current) || current < minimum) current = minimum;
      if (Number.isFinite(maximum) && maximum > 0) {
        current = Math.min(maximum, current);
      }

      const offset = Math.max(0, Math.round((current - minimum) / increment));
      let normalized = minimum + offset * increment;

      if (Number.isFinite(maximum) && maximum > 0 && normalized > maximum) {
        const maxOffset = Math.max(0, Math.floor((maximum - minimum) / increment));
        normalized = minimum + maxOffset * increment;
      }

      input.value = String(normalized);
      return normalized;
    };

    const changeQuantity = (direction) => {
      const minimum = toPositiveNumber(input.min, 1);
      const increment = toPositiveNumber(input.step, 1);
      const maximum = Number(input.max);
      let current = Number(input.value);

      if (!Number.isFinite(current)) current = minimum;

      let next = current + direction * increment;
      next = Math.max(minimum, next);

      if (Number.isFinite(maximum) && maximum > 0) {
        next = Math.min(maximum, next);
      }

      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    root
      .querySelectorAll('[data-md-quantity-minus]')
      .forEach((button) => {
        button.addEventListener('click', () => changeQuantity(-1));
      });

    root
      .querySelectorAll('[data-md-quantity-plus]')
      .forEach((button) => {
        button.addEventListener('click', () => changeQuantity(1));
      });

    input.addEventListener('change', normalizeQuantity);

    const productForm = input.closest('form');
    if (productForm) {
      productForm.addEventListener(
        'submit',
        (event) => {
          normalizeQuantity();
          const addButton = root.querySelector('[data-md-add-button]');
          if (addButton?.disabled) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        },
        true
      );
    }

    if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
      const unsubscribe = window.MinimogEvents.subscribe(
        window.MinimogTheme.pubSubEvents.cartUpdate,
        () => {
          window.setTimeout(() => {
            if (!root.isConnected) {
              if (typeof unsubscribe === 'function') unsubscribe();
              return;
            }
            normalizeQuantity();
          }, 0);
        }
      );
    }

    normalizeQuantity();
  }

  function initVariantSelection(root, galleryApi) {
    const select = root.querySelector('[data-md-variant-select]');
    if (!select) return;

    const price = root.querySelector('[data-md-price]');
    const comparePrice = root.querySelector('[data-md-compare-price]');
    const stock = root.querySelector('[data-md-stock]');
    const sku = root.querySelector('[data-md-sku]');
    const skuRow = root.querySelector('[data-md-sku-row]');
    const addButton = root.querySelector('[data-md-add-button]');
    const addLabel = root.querySelector('[data-md-add-label]');
    const stickyPrice = root.querySelector('[data-md-sticky-price]');
    const stickyAddButton = root.querySelector('[data-md-sticky-add]');
    const stickyAddLabel = root.querySelector('[data-md-sticky-add-label]');
    const volumePricingTrigger = root.querySelector('[data-md-volume-pricing-trigger]');
    const volumePricingThreshold = root.querySelector('[data-md-volume-pricing-threshold]');

    if (addLabel && !addLabel.dataset.defaultLabel) {
      addLabel.dataset.defaultLabel = addLabel.textContent.trim();
    }

    const updateVariant = () => {
      const option = select.options[select.selectedIndex];
      if (!option) return;

      const inStock = option.dataset.inStock === 'true';
      const compareValue = option.dataset.comparePrice || '';

      if (price) price.textContent = option.dataset.price || '';
      if (stickyPrice) stickyPrice.textContent = option.dataset.price || '';

      if (comparePrice) {
        comparePrice.textContent = compareValue;
        comparePrice.hidden = !compareValue;
      }

      if (stock) {
        stock.textContent = inStock ? t('product.inStock', 'In stock') : t('product.outOfStock', 'Out of stock');
        stock.classList.toggle('md-product__stock--unavailable', !inStock);
      }

      const skuValue = (option.dataset.sku || '').trim();
      if (sku) sku.textContent = skuValue;
      if (skuRow) skuRow.hidden = !skuValue;

      if (addButton) addButton.disabled = !inStock;
      if (stickyAddButton) stickyAddButton.disabled = !inStock;

      if (addLabel) {
        addLabel.textContent = inStock
          ? addLabel.dataset.defaultLabel || t('product.addToCart', 'Add to cart')
          : t('product.outOfStock', 'Out of stock');
      }

      if (stickyAddLabel) {
        stickyAddLabel.textContent = inStock
          ? addLabel?.dataset.defaultLabel || t('product.addToCart', 'Add to cart')
          : t('product.outOfStock', 'Out of stock');
      }

      const quantityInput = root.querySelector('[data-md-quantity-input]');
      if (quantityInput) quantityInput.disabled = !inStock;
      root
        .querySelectorAll('[data-md-quantity-minus], [data-md-quantity-plus]')
        .forEach((button) => {
          button.disabled = !inStock;
        });

      setQuantityRules(root, option);

      const hasPriceBreaks = option.dataset.hasPriceBreaks === 'true';
      if (volumePricingTrigger) {
        volumePricingTrigger.hidden = !hasPriceBreaks;
        volumePricingTrigger.setAttribute('aria-hidden', String(!hasPriceBreaks));

        if (hasPriceBreaks && volumePricingThreshold) {
          const firstBreak = option.dataset.firstPriceBreak || '';
          const prefix = volumePricingTrigger.dataset.thresholdPrefix || 'Vanaf';
          const suffix = volumePricingTrigger.dataset.thresholdSuffix || 'stuks extra voordeel';
          volumePricingThreshold.textContent = firstBreak
            ? `${prefix} ${firstBreak} ${suffix}`
            : '';
        }
      }

      document.dispatchEvent(
        new CustomEvent('md:variant-change', {
          detail: {
            productId: String(root.dataset.productId || ''),
            variantId: String(option.value || ''),
            hasPriceBreaks
          }
        })
      );

      if (option.dataset.mediaId && galleryApi) {
        galleryApi.showMedia(option.dataset.mediaId);
      }

      if (window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        window.history.replaceState({}, '', url.toString());
      }
    };

    select.addEventListener('change', updateVariant);
    updateVariant();
  }

  function initStickyCart(root) {
    const stickyBar = root.querySelector('[data-md-sticky-cart]');
    const purchaseCard = root.querySelector('[data-md-purchase-card]');
    const stickyTrigger = root.querySelector('[data-md-sticky-trigger]');
    const stickyAddButton = root.querySelector('[data-md-sticky-add]');
    const originalAddButton = root.querySelector('[data-md-add-button]');

    if (!stickyBar || !purchaseCard || !stickyAddButton || !originalAddButton) {
      return;
    }

    const updateVisibility = () => {
      let shouldShow = false;

      if (stickyTrigger) {
        const triggerRect = stickyTrigger.getBoundingClientRect();
        shouldShow = triggerRect.top <= 0;
      } else {
        const purchaseRect = purchaseCard.getBoundingClientRect();
        shouldShow = purchaseRect.bottom < 0;
      }

      stickyBar.classList.toggle('is-visible', shouldShow);
      stickyBar.setAttribute('aria-hidden', String(!shouldShow));
    };

    stickyAddButton.addEventListener('click', () => {
      if (!originalAddButton.disabled) {
        originalAddButton.click();
      }
    });

    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);
    updateVisibility();
  }

  function initProduct(root) {
    if (!root || root.dataset.mdProductInitialized === 'true') return;
    root.dataset.mdProductInitialized = 'true';

    const galleryApi = initGallery(root);
    initZoom(root);
    initQuantity(root);
    initVariantSelection(root, galleryApi);
    initStickyCart(root);
  }

  function initAll(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initProduct);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
